import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    DndContext,
    KeyboardSensor,
    closestCenter,
    type DragEndEvent,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import AddCollegeModal from "./AddCollegeModal";
import FeaturedContentGrid from "./FeaturedContentGrid";
// @ts-ignore -- JSX module used in TSX page
import CollegeWizard from "../components/colleges/CollegeWizard";
import CollegeChatSearch from "../components/colleges/CollegeChatSearch";
import VideoLightbox from "./VideoLightbox";
import CourseTipsModal from "./CourseTipsModal";
import CourseCompletionModal from "./CourseCompletionModal";
import ExpertModal from "./ExpertModal";
import {
    courseVideoAnalytics,
    moduleVideoAnalytics,
    recordCourseEvent,
    WORDPRESS_TAB,
} from "../services/courseEventService";
import CollegeNotesPanel from "./CollegeNotesPanel";
import CollegeContactsPanel from "./CollegeContactsPanel";
import SortableCollegeRow from "./SortableCollegeRow";
import {
    applyFavouriteRanks,
    compactRemainingFavourites,
    markCollegeFavourite,
    nextFavouriteRank,
    sortAthleteColleges,
} from "./athleteCollegeList";
import { CollegeRankPointerSensor } from "./collegeRankDnd";
import { isAthlete, isGuardian } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import {
    isCollegeChatSearchEnabled,
    isCollegeFinderEnabled,
    isCollegeSearchCompareToggleVisible,
} from "../utils/config";
import {
    athleteProfileFetchPath,
    resolveAthleteCollegeUsername,
    resolveAthleteUserIdForCollegeMutation,
} from "../utils/viewedAthlete";
import { buildApiUrl } from "../utils/apiConfig";
import { buildCourseCompletionPayload, completeCourse } from "../services/courseTaskService";
import { useAppFeatures } from "../hooks/useAppFeatures";
import { useResourceVisibility } from "../hooks/useResourceVisibility";
import type { CollegeContactListItem } from "../types/collegeContacts";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/college-planning.css";
import "../styles/course-cards.css";
import "../styles/expert.css";
import "../styles/college-notes-panel.css";
import "../styles/college-contacts-panel.css";
import "../styles/pagination.css";
import "../styles/athlete-admin-page-spacing.css";
import { apiClient } from "../utils/apiClient";

const COLLEGES_PER_PAGE = 5;
const COLLEGE_SEARCH_MODE_KEY = "collegePlanningSearchMode";

type CollegeSearchMode = "wizard" | "chat";

function readStoredCollegeSearchMode(): CollegeSearchMode | null {
    if (typeof window === "undefined") {
        return null;
    }
    const stored = window.sessionStorage.getItem(COLLEGE_SEARCH_MODE_KEY);
    if (stored === "wizard" || stored === "chat") {
        return stored;
    }
    return null;
}

const COLLEGE_PLANNING_TAB = WORDPRESS_TAB.COLLEGE_PLANNING;

interface Expert {
    id: number;
    slug: string;
    title: string;
    expertImageUrl: string;
    subtitle: string;
    excerpt: string;
    buttonLabel: string;
    buttonUrl: string;
    modalHeading: string;
    modalContent: string;
    modalCloseLabel: string;
}

interface Course {
    id: number;
    slug: string;
    title: string;
    excerpt: string;
    featuredImageUrl: string;
    subtitle: string;
    buttonLabel: string;
    buttonUrl: string;
    launchModalHeading?: string;
    launchModalContent?: string;
    launchModalButtonLabel?: string;
    launchModalUrl?: string;
    courseCompletionModalHeading?: string;
    courseCompletionModalImageUrl?: string;
    courseCompletionModalContent?: string;
    courseCompletionModalBadgeUrl?: string;
    courseCompletionModalDownloadUrl?: string;
    courseCompletionModalCloseLabel?: string;
    courseCompletionModalLinkText?: string;
    courseCompletionModalIconUrl?: string;
    expert: Expert | null;  // NEW - each course has embedded expert
    isCompleted: boolean;
}

interface College {
    id: number;
    college: {
        id: number;
        name: string;
        city: string;
        state: string;
        zip: string;
        schoolUrl: string;
        locale: string;
        size: number;
    };
    addedAt: string;
    isFavourite?: boolean;
    favouriteRank?: number | null;
    recruitingStatus?: string;
    applicationStatus?: string;
    decisionStatus?: string;
}

type RecruitingStatusValue =
    | "NOT_SET"
    | "INTERESTED"
    | "CONTACTED_COACH"
    | "IN_COMMUNICATION"
    | "VISIT_CAMP"
    | "OFFER_RECEIVED"
    | "COMMITTED";
type ApplicationStatusValue = "NOT_STARTED" | "IN_PROGRESS" | "SUBMITTED" | "AWAITING_DECISION";
type DecisionStatusValue = "ACCEPTED" | "WAITLISTED" | "DENIED" | "COMMITTED" | "";

const RECRUITING_STATUS_OPTIONS: {
    value: RecruitingStatusValue;
    menuLabel: string;
    triggerLabel: string;
    statusClass: string;
}[] = [
    { value: "NOT_SET", menuLabel: "--", triggerLabel: "--", statusClass: "status-slate" },
    { value: "INTERESTED", menuLabel: "Interested", triggerLabel: "Interested", statusClass: "status-recruiting-blue" },
    { value: "CONTACTED_COACH", menuLabel: "Contacted Coach", triggerLabel: "Contacted Coach", statusClass: "status-recruiting-blue" },
    { value: "IN_COMMUNICATION", menuLabel: "In Communication", triggerLabel: "In Communication", statusClass: "status-recruiting-blue" },
    { value: "VISIT_CAMP", menuLabel: "Visit / Camp", triggerLabel: "Visit / Camp", statusClass: "status-recruiting-blue" },
    { value: "OFFER_RECEIVED", menuLabel: "Offer Received", triggerLabel: "Offer Received", statusClass: "status-green" },
    { value: "COMMITTED", menuLabel: "Committed", triggerLabel: "Committed", statusClass: "status-green" },
];

function normalizeRecruitingStatus(raw: string | undefined | null): RecruitingStatusValue {
    if (raw == null || raw === "") return "NOT_SET";
    return raw as RecruitingStatusValue;
}

const APPLICATION_STATUS_OPTIONS: {
    value: ApplicationStatusValue;
    menuLabel: string;
    triggerLabel: string;
    statusClass: string;
}[] = [
    { value: "NOT_STARTED", menuLabel: "Not Started", triggerLabel: "Not Started", statusClass: "status-app-not-started" },
    { value: "IN_PROGRESS", menuLabel: "In Progress", triggerLabel: "In Progress", statusClass: "status-app-in-progress" },
    { value: "SUBMITTED", menuLabel: "✅ Submitted", triggerLabel: "✅ Submitted", statusClass: "status-app-submitted" },
    { value: "AWAITING_DECISION", menuLabel: "⏰ Awaiting Decision", triggerLabel: "⏰ Awaiting Decision", statusClass: "status-app-awaiting" },
];

const DECISION_STATUS_OPTIONS: {
    value: Exclude<DecisionStatusValue, "">;
    menuLabel: string;
    triggerLabel: string;
    statusClass: string;
}[] = [
    { value: "ACCEPTED", menuLabel: "Accepted ✅", triggerLabel: "Accepted ✅", statusClass: "status-green" },
    { value: "WAITLISTED", menuLabel: "Waitlisted ⌛", triggerLabel: "Waitlisted ⌛", statusClass: "status-decision-waitlist" },
    { value: "DENIED", menuLabel: "Denied ❌", triggerLabel: "Denied ❌", statusClass: "status-red" },
    { value: "COMMITTED", menuLabel: "Committed 🎯", triggerLabel: "Committed 🎯", statusClass: "status-decision-committed" },
];

function normalizeDecisionStatus(raw: string | undefined | null): DecisionStatusValue {
    if (!raw || raw === "NOT_SET") return "";
    if (raw === "DECLINED") return "DENIED";
    return raw as DecisionStatusValue;
}

function decisionStatusForRequest(value: DecisionStatusValue): Exclude<DecisionStatusValue, ""> | "NOT_SET" {
    return value ? value : "NOT_SET";
}

const DEFAULT_APPLICATION_STATUS: ApplicationStatusValue = "NOT_STARTED";

const recruitingStatusClassMap: Record<RecruitingStatusValue, string> = {
    NOT_SET: "status-slate",
    INTERESTED: "status-recruiting-blue",
    CONTACTED_COACH: "status-recruiting-blue",
    IN_COMMUNICATION: "status-recruiting-blue",
    VISIT_CAMP: "status-recruiting-blue",
    OFFER_RECEIVED: "status-green",
    COMMITTED: "status-green",
};

const applicationStatusClassMap: Record<ApplicationStatusValue, string> = {
    NOT_STARTED: "status-app-not-started",
    IN_PROGRESS: "status-app-in-progress",
    SUBMITTED: "status-app-submitted",
    AWAITING_DECISION: "status-app-awaiting",
};

const decisionStatusClassMap: Record<Exclude<DecisionStatusValue, "">, string> = {
    ACCEPTED: "status-green",
    WAITLISTED: "status-decision-waitlist",
    DENIED: "status-red",
    COMMITTED: "status-decision-committed",
};

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

interface Note {
    id: number;
    name: string;
    content: string;
    date: string;
}

interface CollegeDetailsLite {
    id: number;
    name: string;
    schoolUrl: string | null;
}

type CollegeInsightType = "deadlines" | "scholarships";
type PipelineMenuType = "recruiting" | "application" | "decision";

export default function CollegePlanningPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { userInfo, isLoading: authLoading } = useAuth();
    const { features: appFeatures, loading: appFeaturesLoading } = useAppFeatures();
    const [courses, setCourses] = useState<Course[]>([]);
    const [expert, setExpert] = useState<Expert | null>(null);
    const [colleges, setColleges] = useState<College[]>([]);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rankingError, setRankingError] = useState<string | null>(null);
    const [pipelineError, setPipelineError] = useState<string | null>(null);
    const [currentCoursePage, setCurrentCoursePage] = useState(1);
    const [isAddCollegeModalOpen, setIsAddCollegeModalOpen] = useState(false);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [athleteUsername, setAthleteUsername] = useState<string>("");
    const [athleteNumericId, setAthleteNumericId] = useState<number | null>(null);
    /** User.id for college favourite/pipeline/delete APIs — never AthleteProfile.id. */
    const [athleteUserId, setAthleteUserId] = useState<string | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);
    const [isCourseTipsModalOpen, setIsCourseTipsModalOpen] = useState(false);
    const [pendingCourse, setPendingCourse] = useState<Course | null>(null);
    const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
    const [isExpertModalOpen, setIsExpertModalOpen] = useState(false);
    const [activeCollegeInsight, setActiveCollegeInsight] = useState<{ collegeId: number; type: CollegeInsightType } | null>(null);
    const [collegeDetailsMap, setCollegeDetailsMap] = useState<Record<number, CollegeDetailsLite>>({});
    const collegeDetailsRef = useRef<Record<number, CollegeDetailsLite>>({});
    const [loadingInsightCollegeId, setLoadingInsightCollegeId] = useState<number | null>(null);
    const [notesPanel, setNotesPanel] = useState<{
        isOpen: boolean;
        collegeName: string;
        collegeId: number | null;
    }>({
        isOpen: false,
        collegeName: '',
        collegeId: null
    });
    const [collegeNotes, setCollegeNotes] = useState<Record<number, Note[]>>({});
    const [contactsPanel, setContactsPanel] = useState<{
        isOpen: boolean;
        collegeName: string;
        collegeId: number | null;
    }>({
        isOpen: false,
        collegeName: "",
        collegeId: null
    });
    const [collegeContacts, setCollegeContacts] = useState<Record<number, CollegeContactListItem[]>>({});
    const [updatingPipelineForCollegeId, setUpdatingPipelineForCollegeId] = useState<number | null>(null);
    const [openPipelineMenu, setOpenPipelineMenu] = useState<{ collegeId: number; type: PipelineMenuType } | null>(null);
    const [currentCollegePage, setCurrentCollegePage] = useState(1);

    useEffect(() => {
        collegeDetailsRef.current = collegeDetailsMap;
    }, [collegeDetailsMap]);

    useEffect(() => {
        if (!openPipelineMenu) return undefined;
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Element | null;
            if (target?.closest(".college-pipeline-menu-wrap")) return;
            setOpenPipelineMenu(null);
        };
        document.addEventListener("mousedown", onPointerDown, true);
        return () => document.removeEventListener("mousedown", onPointerDown, true);
    }, [openPipelineMenu]);

    useEffect(() => {
        if (!activeCollegeInsight) return undefined;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setActiveCollegeInsight(null);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [activeCollegeInsight]);

    // Check if current user is admin/staff viewing an athlete profile
    const isAdminViewingAthlete = !isAthlete() && !isGuardian() && Boolean(athleteId);
    const guardianReadOnly = isGuardian();
    const collegeFinderEnabled =
        !appFeaturesLoading &&
        appFeatures?.collegeFinderEnabled === true &&
        isCollegeFinderEnabled();
    const collegeChatSearchEnabled =
        !appFeaturesLoading &&
        appFeatures?.collegeChatSearchEnabled === true &&
        isCollegeChatSearchEnabled();
    const compareToggleVisible =
        !guardianReadOnly &&
        isCollegeSearchCompareToggleVisible() &&
        collegeFinderEnabled &&
        collegeChatSearchEnabled;
    const [searchMode, setSearchMode] = useState<CollegeSearchMode>(() => {
        if (compareToggleVisible) {
            return readStoredCollegeSearchMode() ?? (collegeChatSearchEnabled ? "chat" : "wizard");
        }
        return collegeChatSearchEnabled ? "chat" : "wizard";
    });
    const useChatSearch = !guardianReadOnly && collegeChatSearchEnabled && (!compareToggleVisible || searchMode === "chat");
    const useWizardSearch = !guardianReadOnly && collegeFinderEnabled && !useChatSearch;

    useEffect(() => {
        if (appFeaturesLoading) {
            return;
        }
        if (compareToggleVisible) {
            setSearchMode(readStoredCollegeSearchMode() ?? (collegeChatSearchEnabled ? "chat" : "wizard"));
            return;
        }
        setSearchMode(collegeChatSearchEnabled ? "chat" : "wizard");
    }, [appFeaturesLoading, compareToggleVisible, collegeChatSearchEnabled]);

    const selectSearchMode = useCallback((mode: CollegeSearchMode) => {
        setSearchMode(mode);
        if (mode === "chat") {
            setIsWizardOpen(false);
        }
    }, []);

    useEffect(() => {
        if (!compareToggleVisible) {
            return;
        }
        window.sessionStorage.setItem(COLLEGE_SEARCH_MODE_KEY, searchMode);
    }, [compareToggleVisible, searchMode]);

    // College counseling courses & resources are hidden when the school disables them.
    // Null (loading/failed) is treated as "show" so content is never hidden by accident.
    const { visibility: resourceVisibility } = useResourceVisibility();
    const collegeResourcesEnabled = resourceVisibility?.collegeResourcesEnabled !== false;

    // Athletes see 2 courses per page, admins see 3
    const COURSES_PER_PAGE = isAthlete() || isGuardian() ? 2 : 3;

    // Get team member info from navigation state
    const navState = location.state as AthleteAdminNavState | null;

    const athleteBreadcrumbName = useAdminAthleteDisplayName(
        athleteId,
        navState?.athleteDisplayName
    );

    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    // Helper function to check if a URL is a video URL
    const isVideoUrl = (url: string | null): boolean => {
        if (!url) return false;
        const videoPatterns = [
            /vimeo\.com/i,
            /wistia\.com/i,
            /youtube\.com/i,
            /youtu\.be/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];
        return videoPatterns.some(pattern => pattern.test(url));
    };

    // Helper function to check if a URL is a downloadable document
    const isDocumentUrl = (url: string | null): boolean => {
        if (!url) return false;
        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /\.csv$/i,
            /\.txt$/i,
            /\.zip$/i,
            /\.rar$/i
        ];
        return documentPatterns.some(pattern => pattern.test(url));
    };

    // Handler for opening expert modal
    const handleLearnMore = (expert: Expert) => {
        setExpert(expert);
        setIsExpertModalOpen(true);
    };

    // Handler for course launch button clicks
    const handleCourseLaunch = (course: Course) => {
        if (!course.buttonUrl) return;

        // Show the tips modal and store the course
        setPendingCourse(course);
        setIsCourseTipsModalOpen(true);
    };

    // Handler for actually launching the course after tips modal
    const handleLaunchCourseAfterTips = async () => {
        if (!pendingCourse || !pendingCourse.buttonUrl) return;

        console.log('📹 Launching course:', pendingCourse.title);
        console.log('📹 Button URL:', pendingCourse.buttonUrl);
        console.log('📹 Is video URL?', isVideoUrl(pendingCourse.buttonUrl));

        // Close the tips modal first
        setIsCourseTipsModalOpen(false);

        // If it's a video URL, open in lightbox
        if (isVideoUrl(pendingCourse.buttonUrl)) {
            console.log('📹 Opening video in lightbox with resourceId:', pendingCourse.id);
            setSelectedVideo({
                id: pendingCourse.id,
                title: pendingCourse.title,
                vimeoUrl: pendingCourse.buttonUrl
            });
        } else if (isDocumentUrl(pendingCourse.buttonUrl)) {
            recordCourseEvent({
                wordpressTab: COLLEGE_PLANNING_TAB,
                courseId: pendingCourse.id,
                contentId: pendingCourse.id,
                contentType: "PDF",
                eventType: "PDF_DOWNLOAD",
            });
            // If it's a document, trigger download
            const link = document.createElement('a');
            link.href = pendingCourse.buttonUrl;
            link.download = ''; // Browser will use filename from URL
            link.target = '_blank'; // Fallback if download doesn't work
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            recordCourseEvent({
                wordpressTab: COLLEGE_PLANNING_TAB,
                courseId: pendingCourse.id,
                contentId: pendingCourse.id,
                contentType: "LINK",
                eventType: "LINK_CLICK",
            });
            // Otherwise, open URL in new tab (web pages, external links)
            window.open(pendingCourse.buttonUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Handler for breadcrumb navigation to team member view
    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    // Handler for when video ends
    const handleVideoEnd = (resourceId: number) => {
        const course = pendingCourse ?? courses.find((item) => item.id === resourceId) ?? null;
        if (course) {
            setCourses((prev) =>
                prev.map((item) => (item.id === course.id ? { ...item, isCompleted: true } : item)),
            );
            void completeCourse(buildCourseCompletionPayload(course)).catch((error) => {
                console.error("Failed to persist college planning course completion:", error);
            });
        }

        setSelectedVideo(null);
        setTimeout(() => {
            setIsCompletionModalOpen(true);
        }, 300);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (authLoading && !athleteId) {
                return;
            }
            try {
                setLoading(true);
                setError(null);
                const authUsername = userInfo?.userId?.trim() || "";
                const headers: HeadersInit = { Accept: "application/json" };

                // Get team member info from navigation state if available
                if (navState?.teamMemberName) {
                    // Use the team member name from navigation state
                    const nameParts = navState.teamMemberName.split(' ');
                    setCoachData({
                        id: navState.teamMemberId,
                        firstName: nameParts[0] || navState.teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                // Determine which athlete username/email to use (self, admin URL, or guardian-linked athlete).
                // Always load the profile when possible so college mutations can use User.id (not Profile.id).
                // Never use numeric User.id as the college-list username — that API keys by email.
                let targetAthleteUsername = "";
                const profilePath = athleteProfileFetchPath(athleteId);
                if (profilePath) {
                    try {
                        const athleteRes = await apiClient(buildApiUrl(profilePath), {
                            headers,
                            credentials: "include",
                        });

                        if (athleteRes.ok) {
                            const athleteData = await athleteRes.json();
                            targetAthleteUsername = resolveAthleteCollegeUsername(
                                athleteId && navState?.athleteEmail ? navState.athleteEmail : null,
                                athleteData.username,
                                athleteData.email,
                                authUsername,
                            );
                            if (athleteData.id) {
                                setAthleteNumericId(athleteData.id);
                            } else if (athleteId && /^\d+$/.test(athleteId)) {
                                setAthleteNumericId(parseInt(athleteId, 10));
                            }
                            if (athleteData.userId != null) {
                                setAthleteUserId(String(athleteData.userId));
                            } else {
                                setAthleteUserId(null);
                            }
                        } else if (athleteId || isGuardian()) {
                            setError("We couldn't load this athlete's college list. Please try again.");
                            setLoading(false);
                            return;
                        } else {
                            targetAthleteUsername = resolveAthleteCollegeUsername(authUsername);
                        }
                    } catch {
                        if (athleteId || isGuardian()) {
                            setError("We couldn't load this athlete's college list. Please try again.");
                            setLoading(false);
                            return;
                        }
                        targetAthleteUsername = resolveAthleteCollegeUsername(authUsername);
                    }
                } else {
                    targetAthleteUsername = resolveAthleteCollegeUsername(authUsername);
                }

                if (!targetAthleteUsername && !athleteId && !isGuardian()) {
                    setError("We couldn't load your college list. Please refresh the page.");
                    setLoading(false);
                    return;
                }

                setAthleteUsername(targetAthleteUsername);

                // Fetch courses from WordPress CMS with completion status
                try {
                    // Include username parameter to get completion status for athlete
                    const coursesUrl = targetAthleteUsername
                        ? `/api/resources/courses?username=${encodeURIComponent(targetAthleteUsername)}`
                        : `/api/resources/courses`;

                    const coursesRes = await apiClient(
                        buildApiUrl(coursesUrl),
                        {
                            headers,
                            credentials: "include"
                        }
                    );

                    if (coursesRes.ok) {
                        const coursesData = await coursesRes.json();

                        // NEW: Expect Course[] with embedded expert
                        if (Array.isArray(coursesData)) {
                            setCourses(coursesData);
                            // Extract first expert for top-level display (optional)
                            const firstExpert = coursesData[0]?.expert || null;
                            setExpert(firstExpert);
                        } else {
                            setCourses([]);
                            setExpert(null);
                        }
                    } else {
                        if (coursesRes.status === 400) {
                            const errorData = await coursesRes.json().catch(() => ({}));
                        }
                        setCourses([]);
                        setExpert(null);
                    }
                } catch (coursesError) {
                    setCourses([]);
                    setExpert(null);
                }

                // Fetch colleges from Java backend using the correct endpoint
                if (targetAthleteUsername) {
                    try {
                        const collegesRes = await apiClient(
                            buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(targetAthleteUsername)}`),
                            {
                                headers,
                                credentials: "include"
                            }
                        );

                        if (collegesRes.ok) {
                            const collegesData = await collegesRes.json();
                            const list = Array.isArray(collegesData) ? collegesData : [];
                            setColleges(list);
                        } else {
                            setColleges([]);
                        }
                    } catch (collegeError) {
                        setColleges([]);
                    }
                }
            } catch (error) {
                setError("We couldn't load your college list. Please refresh the page.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [athleteId, authLoading, location.state, userInfo?.userId]);

    const handleAddCollege = () => {
        setIsAddCollegeModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsAddCollegeModalOpen(false);
    };

    const handleAddCollegeToList = async (collegeName: string) => {
        // Close modal and refresh the college list
        setIsAddCollegeModalOpen(false);
        await refreshCollegeList();
    };

    const refreshCollegeList = async () => {
        try {
            const headers: HeadersInit = { Accept: "application/json" };

            // Call GET /api/colleges/athlete/{username} to refresh the list
            if (athleteUsername) {
                const collegesRes = await apiClient(
                    buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(athleteUsername)}`),
                    {
                        headers,
                        credentials: "include" // Send HTTP-only cookies
                    }
                );

                if (collegesRes.ok) {
                    const collegesData = await collegesRes.json();
                    setColleges(collegesData);
                } else {
                    console.warn("Could not refresh colleges:", collegesRes.status);
                }
            }
        } catch (error) {
            console.warn("Error refreshing colleges:", error);
        }
    };

    const handleRemoveCollege = async (collegeId: number) => {
        if (guardianReadOnly) return;
        setActiveCollegeInsight((prev) => (prev?.collegeId === collegeId ? null : prev));
        try {
            const userId = localStorage.getItem("userId");
            const targetAthleteId = resolveAthleteUserIdForCollegeMutation(athleteUserId, userId);

            // Remove from backend first
            if (targetAthleteId) {
                try {
                    const response = await apiClient(buildApiUrl(`api/athletes/${encodeURIComponent(targetAthleteId)}/colleges/${collegeId}`), {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json"
                        },
                        credentials: "include" // Send HTTP-only cookies
                    });

                    const removeLocally = (previous: College[]) =>
                        compactRemainingFavourites(previous.filter((college) => college.college.id !== collegeId));
                    if (response.ok) {
                        setColleges(removeLocally);
                        setRankingError(null);
                    } else {
                        setRankingError("We couldn't remove that college. Please try again.");
                    }
                } catch (error) {
                    setRankingError("We couldn't remove that college. Please try again.");
                }
            }
        } catch (error) {
            console.error("Error removing college:", error);
        }
    };

    const handleToggleFavorite = async (collegeId: number) => {
        if (guardianReadOnly) return;
        try {
            const userId = localStorage.getItem("userId");
            const targetAthleteId = resolveAthleteUserIdForCollegeMutation(athleteUserId, userId);

            const college = colleges.find((item) => item.college.id === collegeId);
            if (!college) return;

            const newFavoriteStatus = !college.isFavourite;
            const previousColleges = colleges;
            const optimisticRank = newFavoriteStatus ? nextFavouriteRank(colleges) : null;
            setRankingError(null);
            setColleges(markCollegeFavourite(colleges, collegeId, newFavoriteStatus, optimisticRank));

            if (targetAthleteId) {
                try {
                    const response = await apiClient(
                        buildApiUrl(`/api/athletes/${encodeURIComponent(targetAthleteId)}/colleges/${collegeId}`),
                        {
                            method: "PUT",
                            headers: {
                                "Content-Type": "application/json",
                                Accept: "application/json"
                            },
                            credentials: "include",
                            body: JSON.stringify({ isFavourite: newFavoriteStatus })
                        }
                    );

                    if (!response.ok) {
                        setColleges(previousColleges);
                        setRankingError("We couldn't update that favorite. Please try again.");
                        return;
                    }
                    const updated = await response.json() as { isFavourite?: boolean; favouriteRank?: number | null };
                    setColleges((current) =>
                        markCollegeFavourite(
                            current,
                            collegeId,
                            Boolean(updated.isFavourite),
                            updated.favouriteRank ?? (updated.isFavourite ? optimisticRank : null),
                        ),
                    );
                } catch {
                    setColleges(previousColleges);
                    setRankingError("We couldn't update that favorite. Please try again.");
                }
            }
        } catch {
            setRankingError("We couldn't update that favorite. Please try again.");
        }
    };

    const persistFavouriteOrder = async (orderedFavouriteIds: number[]) => {
        if (guardianReadOnly || orderedFavouriteIds.length < 2) {
            return;
        }
        const previousColleges = colleges;
        setRankingError(null);
        setColleges(applyFavouriteRanks(colleges, orderedFavouriteIds));

        const userId = localStorage.getItem("userId");
        const targetAthleteId = resolveAthleteUserIdForCollegeMutation(athleteUserId, userId);
        if (!targetAthleteId) {
            setColleges(previousColleges);
            setRankingError("We couldn't save your school ranking. Please try again.");
            return;
        }

        try {
            const response = await apiClient(
                buildApiUrl(`/api/athletes/${encodeURIComponent(targetAthleteId)}/college-favourites/order`),
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({ collegeIds: orderedFavouriteIds }),
                },
            );
            if (!response.ok) {
                setColleges(previousColleges);
                setRankingError("We couldn't save your school ranking. Please try again.");
                return;
            }
            const updatedList = await response.json() as College[];
            if (Array.isArray(updatedList) && updatedList.length > 0) {
                setColleges(updatedList);
            }
        } catch {
            setColleges(previousColleges);
            setRankingError("We couldn't save your school ranking. Please try again.");
        }
    };

    const handlePipelineStatusChange = async (
        collegeId: number,
        updates: { recruitingStatus?: RecruitingStatusValue; applicationStatus?: ApplicationStatusValue; decisionStatus?: DecisionStatusValue }
    ) => {
        if (guardianReadOnly) return;
        setOpenPipelineMenu(null);
        const userId = localStorage.getItem("userId");
        const targetAthleteId = resolveAthleteUserIdForCollegeMutation(athleteUserId, userId);
        if (!targetAthleteId) {
            return;
        }

        const existing = colleges.find((c) => c.college.id === collegeId);
        if (!existing) {
            return;
        }

        const previousRecruitingStatus = normalizeRecruitingStatus(existing.recruitingStatus);
        const previousApplicationStatus = (existing.applicationStatus as ApplicationStatusValue) || DEFAULT_APPLICATION_STATUS;
        const previousDecisionStatus = normalizeDecisionStatus(existing.decisionStatus);

        const nextRecruitingStatus = updates.recruitingStatus ?? previousRecruitingStatus;
        const nextApplicationStatus = updates.applicationStatus ?? previousApplicationStatus;
        const nextDecisionStatus = updates.decisionStatus !== undefined
            ? normalizeDecisionStatus(updates.decisionStatus)
            : previousDecisionStatus;

        setUpdatingPipelineForCollegeId(collegeId);
        setPipelineError(null);
        setColleges((prev) =>
            prev.map((row) =>
                row.college.id === collegeId
                    ? {
                        ...row,
                        recruitingStatus: nextRecruitingStatus,
                        applicationStatus: nextApplicationStatus,
                        decisionStatus: nextDecisionStatus
                    }
                    : row
            )
        );

        try {
            const response = await apiClient(
                buildApiUrl(`/api/athletes/${encodeURIComponent(targetAthleteId)}/colleges/${collegeId}/pipeline`),
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        recruitingStatus: nextRecruitingStatus,
                        applicationStatus: nextApplicationStatus,
                        decisionStatus: decisionStatusForRequest(nextDecisionStatus)
                    })
                }
            );

            if (!response.ok) {
                throw new Error("Failed to update pipeline status");
            }

            const updated = await response.json();
            setColleges((prev) =>
                prev.map((row) =>
                    row.college.id === collegeId
                        ? {
                            ...row,
                            recruitingStatus: updated.recruitingStatus ?? nextRecruitingStatus,
                            applicationStatus: updated.applicationStatus ?? nextApplicationStatus,
                            decisionStatus: normalizeDecisionStatus(
                                updated.decisionStatus ?? nextDecisionStatus
                            )
                        }
                        : row
                )
            );
        } catch {
            setColleges((prev) =>
                prev.map((row) =>
                    row.college.id === collegeId
                        ? {
                            ...row,
                            recruitingStatus: previousRecruitingStatus,
                            applicationStatus: previousApplicationStatus,
                            decisionStatus: previousDecisionStatus
                        }
                        : row
                )
            );
            setPipelineError("We couldn't update that school's status. Please try again.");
        } finally {
            setUpdatingPipelineForCollegeId(null);
        }
    };

    const handleNotesClick = (collegeId: number, collegeName: string) => {
        setNotesPanel({
            isOpen: true,
            collegeName,
            collegeId
        });
    };

    const handleCloseNotes = () => {
        setNotesPanel({
            isOpen: false,
            collegeName: '',
            collegeId: null
        });
    };

    const handleNotesLoaded = useCallback((collegeId: number, notes: Note[]) => {
        setCollegeNotes(prev => ({
            ...prev,
            [collegeId]: notes
        }));
    }, []);

    const handleAddNote = useCallback((note: Note) => {
        if (notesPanel.collegeId !== null) {
            setCollegeNotes(prev => {
                const existingNotes = prev[notesPanel.collegeId!] || [];
                // Add new note at the beginning (most recent first)
                return {
                    ...prev,
                    [notesPanel.collegeId!]: [note, ...existingNotes]
                };
            });
        }
    }, [notesPanel.collegeId]);

    const handleUpdateNote = useCallback((updated: Note) => {
        if (notesPanel.collegeId !== null) {
            setCollegeNotes(prev => ({
                ...prev,
                [notesPanel.collegeId!]: (prev[notesPanel.collegeId!] || []).map(note =>
                    note.id === updated.id ? updated : note
                )
            }));
        }
    }, [notesPanel.collegeId]);

    const handleDeleteNote = useCallback((noteId: number) => {
        if (notesPanel.collegeId !== null) {
            setCollegeNotes(prev => ({
                ...prev,
                [notesPanel.collegeId!]: (prev[notesPanel.collegeId!] || []).filter(
                    note => note.id !== noteId
                )
            }));
        }
    }, [notesPanel.collegeId]);

    const handleContactsClick = (collegeId: number, collegeName: string) => {
        setContactsPanel({
            isOpen: true,
            collegeName,
            collegeId
        });
    };

    const handleCloseContacts = () => {
        setContactsPanel({
            isOpen: false,
            collegeName: "",
            collegeId: null
        });
    };

    const handleContactsLoaded = useCallback((collegeId: number, contacts: CollegeContactListItem[]) => {
        setCollegeContacts(prev => ({
            ...prev,
            [collegeId]: contacts
        }));
    }, []);

    const handleDeleteContact = useCallback((contactId: number) => {
        if (contactsPanel.collegeId !== null) {
            setCollegeContacts(prev => ({
                ...prev,
                [contactsPanel.collegeId!]: (prev[contactsPanel.collegeId!] || []).filter(
                    c => c.id !== contactId
                )
            }));
        }
    }, [contactsPanel.collegeId]);

    const handleDataClick = (college: College) => {
        // Open College Scorecard in a new tab
        window.open(`https://collegescorecard.ed.gov/school?${college.college.id}`, '_blank', 'noopener,noreferrer');
    };

    const ensureCollegeDetails = useCallback(async (collegeId: number): Promise<CollegeDetailsLite | null> => {
        const cached = collegeDetailsRef.current[collegeId];
        if (cached) {
            return cached;
        }
        try {
            setLoadingInsightCollegeId(collegeId);
            const response = await apiClient(buildApiUrl(`/api/colleges/college/${encodeURIComponent(collegeId)}`), {
                headers: { Accept: "application/json" },
                credentials: "include"
            });
            if (!response.ok) {
                return null;
            }
            const details = await response.json() as CollegeDetailsLite;
            collegeDetailsRef.current = { ...collegeDetailsRef.current, [collegeId]: details };
            setCollegeDetailsMap((prev) => ({ ...prev, [collegeId]: details }));
            return details;
        } catch {
            return null;
        } finally {
            setLoadingInsightCollegeId(null);
        }
    }, []);

    const openCollegeInsight = async (collegeId: number, type: CollegeInsightType) => {
        await ensureCollegeDetails(collegeId);
        setActiveCollegeInsight({ collegeId, type });
    };

    const collegeAdmissionsUrl = (collegeId: number): string | null => {
        const schoolUrl = collegeDetailsMap[collegeId]?.schoolUrl;
        if (!schoolUrl) return null;
        return schoolUrl.startsWith("http") ? schoolUrl : `https://${schoolUrl}`;
    };

    // Pagination logic for courses
    const totalCoursePages = Math.ceil(courses.length / COURSES_PER_PAGE);
    const currentCourses = courses.slice(
        (currentCoursePage - 1) * COURSES_PER_PAGE,
        currentCoursePage * COURSES_PER_PAGE
    );

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="College Planning"
            teamMemberLabel={getTeamMemberBreadcrumbLabel(
                coachData,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            )}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteBreadcrumbName}
            athleteProfileId={athleteId}
        />
    ) : null;

    const displayedColleges = useMemo(() => sortAthleteColleges(colleges), [colleges]);
    const favouriteColleges = useMemo(
        () => displayedColleges.filter((college) => Boolean(college.isFavourite)),
        [displayedColleges],
    );
    const unstarredColleges = useMemo(
        () => displayedColleges.filter((college) => !college.isFavourite),
        [displayedColleges],
    );
    const favouriteSortable = favouriteColleges.length >= 2 && !guardianReadOnly;

    const sensors = useSensors(
        useSensor(CollegeRankPointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleFavouriteDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = favouriteColleges.findIndex((college) => college.college.id === active.id);
        const newIndex = favouriteColleges.findIndex((college) => college.college.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        const reordered = arrayMove(favouriteColleges, oldIndex, newIndex);
        void persistFavouriteOrder(reordered.map((college) => college.college.id));
    };

    const totalCollegePages = Math.max(1, Math.ceil(unstarredColleges.length / COLLEGES_PER_PAGE));

    useEffect(() => {
        setCurrentCollegePage((prev) => Math.min(prev, totalCollegePages));
    }, [totalCollegePages]);

    const pagedUnstarredColleges = useMemo(() => {
        const start = (currentCollegePage - 1) * COLLEGES_PER_PAGE;
        return unstarredColleges.slice(start, start + COLLEGES_PER_PAGE);
    }, [unstarredColleges, currentCollegePage]);

    const visibleColleges = useMemo(
        () => [...favouriteColleges, ...pagedUnstarredColleges],
        [favouriteColleges, pagedUnstarredColleges],
    );

    const collegePageStart = unstarredColleges.length === 0
        ? 0
        : (currentCollegePage - 1) * COLLEGES_PER_PAGE + 1;
    const collegePageEnd = Math.min(currentCollegePage * COLLEGES_PER_PAGE, unstarredColleges.length);

    const labelForRecruitingTrigger = (value: RecruitingStatusValue) =>
        RECRUITING_STATUS_OPTIONS.find((o) => o.value === value)?.triggerLabel ?? "--";

    const labelForApplicationTrigger = (value: ApplicationStatusValue) =>
        APPLICATION_STATUS_OPTIONS.find((o) => o.value === value)?.triggerLabel ?? "Not Started";

    const labelForDecisionTrigger = (value: DecisionStatusValue) => {
        if (!value) return "--";
        return DECISION_STATUS_OPTIONS.find((o) => o.value === value)?.triggerLabel ?? "--";
    };

    if (loading) {
        return (
            <div className={`college-planning-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                {adminBreadcrumb}
                <div className="college-planning-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`college-planning-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                {adminBreadcrumb}
                <div className="college-planning-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`college-planning-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
            {adminBreadcrumb}

            <div className={`college-planning-container ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>

                {/* Breadcrumb Navigation - Hidden for athletes viewing their own profile */}
                {/* Removed per requirement: Athletes should not see breadcrumbs */}

                {/* Courses Section */}
                {/*<section className="courses-section">*/}
                {/*    <div className="courses-header">*/}
                {/*        <h2>Courses</h2>*/}
                {/*    </div>*/}

                {/*        /!* Courses Grid *!/*/}
                {/*        <div className="courses-content-layout">*/}
                {/*            <div className="courses-grid" data-testid="courses-grid">*/}
                {/*                {currentCourses.map((course) => (*/}
                {/*                    <div key={course.id} className="course-card" data-testid={`course-${course.slug}`}>*/}
                {/*                        <div className="course-image">*/}
                {/*                            <img src={course.featuredImageUrl} alt={course.title} />*/}
                {/*                        </div>*/}
                {/*                        <div className="course-content">*/}
                {/*                            /!* Expert info and completion badge row *!/*/}
                {/*                            <div className="course-top-row">*/}
                {/*                                {course.expert && (*/}
                {/*                                    <div className="course-expert-info">*/}
                {/*                                        <div className="course-expert-avatar">*/}
                {/*                                            <img src={course.expert.expertImageUrl} alt={course.expert.title} />*/}
                {/*                                        </div>*/}
                {/*                                        <div className="course-expert-details">*/}
                {/*                                            <button*/}
                {/*                                                className="course-expert-name-link"*/}
                {/*                                                onClick={() => handleLearnMore(course.expert!)}*/}
                {/*                                                data-testid={`course-expert-link-${course.id}`}*/}
                {/*                                            >*/}
                {/*                                                {course.expert.title}*/}
                {/*                                            </button>*/}
                {/*                                        </div>*/}
                {/*                                    </div>*/}
                {/*                                )}*/}

                {/*                                {course.isCompleted && (*/}
                {/*                                    <div className="course-completion-badge" data-testid={`course-completed-${course.id}`}>*/}
                {/*                                        <span className="completion-checkmark">✓</span>*/}
                {/*                                        <span className="completion-text">Complete</span>*/}
                {/*                                    </div>*/}
                {/*                                )}*/}
                {/*                            </div>*/}

                {/*                            <h4 className="course-name">{course.title}</h4>*/}
                {/*                            <button className="launch-btn" onClick={() => handleCourseLaunch(course)} data-testid={`course-action-${course.id}`}>*/}
                {/*                                {course.buttonLabel}*/}
                {/*                            </button>*/}
                {/*                        </div>*/}
                {/*                    </div>*/}
                {/*                ))}*/}
                {/*            </div>*/}
                {/*        </div>*/}

                {/*        /!* Pagination for Courses *!/*/}
                {/*        {totalCoursePages > 1 && (*/}
                {/*            <div className="pagination">*/}
                {/*                <div className="pagination-info">*/}
                {/*                    <span>*/}
                {/*                        Showing <strong>{(currentCoursePage - 1) * COURSES_PER_PAGE + 1}-{Math.min(currentCoursePage * COURSES_PER_PAGE, courses.length)}</strong> of <strong>{courses.length}</strong> courses*/}
                {/*                    </span>*/}
                {/*                </div>*/}

                {/*                <div className="pagination-controls">*/}
                {/*                    <div className="page-nav">*/}
                {/*                        <button*/}
                {/*                            className="page-btn"*/}
                {/*                            onClick={() => setCurrentCoursePage(Math.max(currentCoursePage - 1, 1))}*/}
                {/*                            disabled={currentCoursePage <= 1}*/}
                {/*                            aria-label="Previous page"*/}
                {/*                        >*/}
                {/*                            Previous*/}
                {/*                        </button>*/}
                {/*                        {Array.from({ length: totalCoursePages }, (_, i) => i + 1).map((page) => (*/}
                {/*                            <button*/}
                {/*                                key={page}*/}
                {/*                                className={`page-number ${page === currentCoursePage ? "page-number-active" : ""}`}*/}
                {/*                                onClick={() => setCurrentCoursePage(page)}*/}
                {/*                                aria-label={`Page ${page}`}*/}
                {/*                                aria-current={page === currentCoursePage ? 'page' : undefined}*/}
                {/*                            >*/}
                {/*                                {page}*/}
                {/*                            </button>*/}
                {/*                        ))}*/}
                {/*                        <button*/}
                {/*                            className="page-btn"*/}
                {/*                            onClick={() => setCurrentCoursePage(Math.min(currentCoursePage + 1, totalCoursePages))}*/}
                {/*                            disabled={currentCoursePage >= totalCoursePages}*/}
                {/*                            aria-label="Next page"*/}
                {/*                        >*/}
                {/*                            Next*/}
                {/*                        </button>*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        )}*/}
                {/*    </section>*/}


                {/* My List Section */}
                <section className="my-list-section">
                    {compareToggleVisible && (
                        <div className="college-search-mode-toggle" role="group" aria-label="College search mode">
                            <span className="college-search-mode-label">Search mode</span>
                            <button
                                type="button"
                                className={searchMode === "chat" ? "is-active" : ""}
                                aria-pressed={searchMode === "chat"}
                                onClick={() => selectSearchMode("chat")}
                            >
                                Chat search
                            </button>
                            <button
                                type="button"
                                className={searchMode === "wizard" ? "is-active" : ""}
                                aria-pressed={searchMode === "wizard"}
                                onClick={() => selectSearchMode("wizard")}
                            >
                                Guided search
                            </button>
                        </div>
                    )}
                    {useChatSearch && (
                        <CollegeChatSearch
                            athleteUsername={athleteUsername}
                            onCollegeSaved={() => {
                                refreshCollegeList();
                            }}
                        />
                    )}
                    {useWizardSearch && isWizardOpen && (
                        <CollegeWizard
                            athleteUsername={athleteUsername}
                            onCollegeSaved={() => {
                                refreshCollegeList();
                            }}
                        />
                    )}

                    {colleges.length === 0 ? (
                        <div className="empty-college-list">
                            <div className="empty-list-icon">📋</div>
                            <h3>{guardianReadOnly ? "College List" : "Create College List"}</h3>
                            <p>
                                {guardianReadOnly
                                    ? "No colleges have been added to this athlete's list yet."
                                    : "Start adding colleges of interests when you are ready to explore."}
                            </p>
                            {!guardianReadOnly && (
                                <div className="empty-college-actions">
                                    {useWizardSearch && (
                                        <button
                                            type="button"
                                            className="add-college-btn-large"
                                            onClick={() => setIsWizardOpen((prev) => !prev)}
                                        >
                                            <span className="add-icon">⊕</span>
                                            {isWizardOpen ? "Hide advanced search" : "Open advanced search"}
                                        </button>
                                    )}
                                    <button type="button" className="add-college-btn-large" onClick={handleAddCollege}>
                                        <span className="add-icon">⊕</span>
                                        Add college
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="my-list-panel">
                            <div className={`my-list-header${isAdminViewingAthlete ? " my-list-header--admin" : ""}`}>
                                <h2>My List</h2>
                                {!guardianReadOnly && (useWizardSearch || !isWizardOpen) && (
                                    <div className="my-list-actions">
                                        {useWizardSearch && (
                                            <button className="add-college-btn" onClick={() => setIsWizardOpen((prev) => !prev)}>
                                                <span className="add-icon">⊕</span>
                                                {isWizardOpen ? "Hide advanced search" : "Open advanced search"}
                                            </button>
                                        )}
                                        {!isWizardOpen && (
                                            <button className="add-college-btn" onClick={handleAddCollege}>
                                                <span className="add-icon">⊕</span>
                                                Add college
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {rankingError && (
                                <div className="college-ranking-error" role="alert">
                                    {rankingError}
                                </div>
                            )}
                            {pipelineError && (
                                <div className="college-ranking-error" role="alert">
                                    {pipelineError}
                                </div>
                            )}
                            {favouriteSortable && (
                                <p className="college-rank-hint">Drag starred schools to rank them.</p>
                            )}
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleFavouriteDragEnd}
                            >
                            <SortableContext
                                items={favouriteColleges.map((college) => college.college.id)}
                                strategy={verticalListSortingStrategy}
                            >
                            <div className="colleges-list">
                            {visibleColleges.map((college) => {
                                const recruitingValue = normalizeRecruitingStatus(college.recruitingStatus);
                                const applicationValue = (college.applicationStatus as ApplicationStatusValue) || DEFAULT_APPLICATION_STATUS;
                                const decisionValue = normalizeDecisionStatus(college.decisionStatus);
                                return (
                                <SortableCollegeRow
                                    key={college.id}
                                    id={college.college.id}
                                    disabled={!college.isFavourite || !favouriteSortable}
                                >
                                {({ setNodeRef, setActivatorNodeRef, style, isDragging, handleProps, rowListeners }) => (
                                <React.Fragment>
                                    <div
                                        ref={setNodeRef}
                                        style={style}
                                        className={`college-row ${isDragging ? "college-row--dragging" : ""} ${college.isFavourite && favouriteSortable ? "college-row--sortable" : ""} ${openPipelineMenu?.collegeId === college.college.id ? "college-row-with-open-pipeline-menu" : ""}`}
                                        {...(rowListeners ?? {})}
                                    >
                                        <div className="college-handle-cell">
                                            {college.isFavourite && handleProps ? (
                                                <div
                                                    ref={setActivatorNodeRef}
                                                    className="college-drag-handle"
                                                    aria-label={`Drag to rank ${college.college.name}`}
                                                    data-testid={`rank-handle-${college.college.id}`}
                                                    {...handleProps}
                                                >
                                                    <span aria-hidden="true">⋮⋮</span>
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className="college-star-cell">
                                            {guardianReadOnly ? (
                                                <span
                                                    className="college-star-btn college-star-btn--readonly"
                                                    aria-label={college.isFavourite ? "Favourited" : "Not favourited"}
                                                    data-testid={`star-${college.college.id}`}
                                                >
                                                    {college.isFavourite ? (
                                                        <span className="star-icon star-filled">&#9733;</span>
                                                    ) : (
                                                        <span className="star-icon star-empty">&#9734;</span>
                                                    )}
                                                </span>
                                            ) : (
                                                <button
                                                    className="college-star-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleFavorite(college.college.id);
                                                    }}
                                                    aria-label={college.isFavourite ? 'Remove from favorites' : 'Add to favorites'}
                                                    data-testid={`star-${college.college.id}`}
                                                >
                                                    {college.isFavourite ? (
                                                        <span className="star-icon star-filled">&#9733;</span>
                                                    ) : (
                                                        <span className="star-icon star-empty">&#9734;</span>
                                                    )}
                                                </button>
                                            )}
                                            {college.isFavourite && college.favouriteRank != null && (
                                                <span
                                                    className="college-rank-badge"
                                                    data-testid={`rank-${college.college.id}`}
                                                >
                                                    {college.favouriteRank}
                                                </span>
                                            )}
                                        </div>
                                        <div className="college-name">{college.college.name}</div>
                                        <div className="college-value college-value--location">{college.college.city}, {college.college.state}</div>
                                        <div className="college-value college-value--type">Private</div>
                                        <div className="college-value college-value--size">{college.college.size > 10000 ? 'Large' : college.college.size > 5000 ? 'Medium' : 'Small'}</div>
                                        <div className="college-row-actions-top">
                                            <button
                                                className="college-action-btn contacts-btn"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleContactsClick(college.college.id, college.college.name);
                                                }}
                                                data-testid={`contacts-${college.college.id}`}
                                            >
                                                <span className="btn-text">My contacts</span>
                                            </button>
                                            <button
                                                className="college-action-btn notes-btn"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleNotesClick(college.college.id, college.college.name);
                                                }}
                                                data-testid={`notes-${college.college.id}`}
                                            >
                                                <span className="btn-text">My notes</span>
                                            </button>
                                        </div>
                                        <div className="college-pipeline-row">
                                            <div className="college-pipeline-menu-wrap">
                                                <button
                                                    type="button"
                                                    className={`college-pipeline-select college-pipeline-select-recruiting ${recruitingValue === "NOT_SET" ? "college-pipeline-select--recruiting-no-chevron" : ""} ${recruitingStatusClassMap[recruitingValue]}`}
                                                    onClick={() => setOpenPipelineMenu((prev) => prev?.collegeId === college.college.id && prev.type === "recruiting" ? null : { collegeId: college.college.id, type: "recruiting" })}
                                                    disabled={guardianReadOnly || updatingPipelineForCollegeId === college.college.id}
                                                >
                                                    Recruiting: {labelForRecruitingTrigger(recruitingValue)}
                                                </button>
                                                {openPipelineMenu?.collegeId === college.college.id && openPipelineMenu.type === "recruiting" && (
                                                    <div className="college-pipeline-dropdown college-pipeline-dropdown--recruiting">
                                                        <div className="college-pipeline-recruiting-header">Recruiting statuses</div>
                                                        <div className="college-pipeline-dropdown-recruiting-scroll" role="listbox" aria-label="Recruiting status options">
                                                            {RECRUITING_STATUS_OPTIONS.map((option) => (
                                                                <button
                                                                    key={option.value}
                                                                    type="button"
                                                                    role="option"
                                                                    aria-selected={recruitingValue === option.value}
                                                                    className={`college-pipeline-option ${option.statusClass}${recruitingValue === option.value ? " college-pipeline-option--recruiting-selected" : ""}`}
                                                                    onClick={() => handlePipelineStatusChange(college.college.id, { recruitingStatus: option.value })}
                                                                >
                                                                    Recruiting: {option.menuLabel}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="college-pipeline-menu-wrap">
                                                <button
                                                    type="button"
                                                    className={`college-pipeline-select college-pipeline-select-app ${applicationStatusClassMap[applicationValue]}`}
                                                    onClick={() => setOpenPipelineMenu((prev) => prev?.collegeId === college.college.id && prev.type === "application" ? null : { collegeId: college.college.id, type: "application" })}
                                                    disabled={guardianReadOnly || updatingPipelineForCollegeId === college.college.id}
                                                >
                                                    App: {labelForApplicationTrigger(applicationValue)}
                                                </button>
                                                {openPipelineMenu?.collegeId === college.college.id && openPipelineMenu.type === "application" && (
                                                    <div className="college-pipeline-dropdown college-pipeline-dropdown--application">
                                                        <div className="college-pipeline-application-header">📝 Application Stage</div>
                                                        {APPLICATION_STATUS_OPTIONS.map((option) => (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                className={`college-pipeline-option ${option.statusClass}${applicationValue === option.value ? " college-pipeline-option--application-selected" : ""}`}
                                                                onClick={() => handlePipelineStatusChange(college.college.id, { applicationStatus: option.value })}
                                                            >
                                                                App: {option.menuLabel}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="college-pipeline-menu-wrap">
                                                <button
                                                    type="button"
                                                    className={`college-pipeline-select college-pipeline-select-decision ${decisionValue ? decisionStatusClassMap[decisionValue as Exclude<DecisionStatusValue, "">] : "status-slate"}`}
                                                    onClick={() => setOpenPipelineMenu((prev) => prev?.collegeId === college.college.id && prev.type === "decision" ? null : { collegeId: college.college.id, type: "decision" })}
                                                    disabled={guardianReadOnly || updatingPipelineForCollegeId === college.college.id}
                                                >
                                                    Decision Stage: {labelForDecisionTrigger(decisionValue)}
                                                </button>
                                                {openPipelineMenu?.collegeId === college.college.id && openPipelineMenu.type === "decision" && (
                                                    <div className="college-pipeline-dropdown college-pipeline-dropdown--decision-stage">
                                                        <div className="college-pipeline-decision-header">🎉 Decision Stage</div>
                                                        <button
                                                            type="button"
                                                            className={`college-pipeline-option college-pipeline-option--decision-clear status-slate${decisionValue === "" ? " college-pipeline-option--decision-selected" : ""}`}
                                                            onClick={() => handlePipelineStatusChange(college.college.id, { decisionStatus: "" })}
                                                        >
                                                            Decision: --
                                                        </button>
                                                        {DECISION_STATUS_OPTIONS.map((option) => (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                className={`college-pipeline-option ${option.statusClass}${decisionValue === option.value ? " college-pipeline-option--decision-selected" : ""}`}
                                                                onClick={() => handlePipelineStatusChange(college.college.id, { decisionStatus: option.value })}
                                                            >
                                                                {option.menuLabel}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="college-row-icon-actions">
                                            <button
                                                type="button"
                                                className="college-row-icon-btn"
                                                data-tooltip="Application deadlines"
                                                aria-label="Application Deadlines"
                                                data-testid={`deadlines-${college.college.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openCollegeInsight(college.college.id, "deadlines");
                                                }}
                                            >
                                                <svg className="college-row-icon college-row-icon--calendar" viewBox="0 0 24 24" aria-hidden="true">
                                                    <rect x="3" y="4" width="18" height="18" rx="3" fill="#ffffff" stroke="#d1d5db" strokeWidth="1.5" />
                                                    <rect x="3" y="4" width="18" height="7" rx="3" fill="#ef4444" />
                                                    <rect x="3" y="8" width="18" height="3" fill="#ef4444" />
                                                    <text x="12" y="19" textAnchor="middle" fontSize="8" fontWeight="700" fill="#374151" fontFamily="system-ui, sans-serif">17</text>
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                className="college-row-icon-btn"
                                                data-tooltip="Scholarships"
                                                aria-label="Scholarships"
                                                data-testid={`scholarships-${college.college.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openCollegeInsight(college.college.id, "scholarships");
                                                }}
                                            >
                                                <svg className="college-row-icon college-row-icon--money" viewBox="0 0 24 24" aria-hidden="true">
                                                    <rect x="4" y="10" width="16" height="9" rx="1.5" fill="#86efac" stroke="#16a34a" strokeWidth="1" />
                                                    <rect x="3" y="7.5" width="16" height="9" rx="1.5" fill="#4ade80" stroke="#16a34a" strokeWidth="1" />
                                                    <rect x="2" y="5" width="16" height="9" rx="1.5" fill="#22c55e" stroke="#15803d" strokeWidth="1" />
                                                    <circle cx="10" cy="9.5" r="2.2" fill="#bbf7d0" stroke="#15803d" strokeWidth="0.8" />
                                                    <text x="10" y="11" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#15803d" fontFamily="system-ui, sans-serif">$</text>
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                className="college-row-icon-btn"
                                                data-tooltip="Data"
                                                aria-label="Data"
                                                data-testid={`data-${college.college.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDataClick(college);
                                                }}
                                            >
                                                <svg className="college-row-icon college-row-icon--chart" viewBox="0 0 24 24" aria-hidden="true">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1" />
                                                    <polyline points="5 16 10 11 13 14 19 7" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <circle cx="19" cy="7" r="1.5" fill="#ef4444" />
                                                </svg>
                                            </button>
                                            {!guardianReadOnly && (
                                            <button
                                                type="button"
                                                className="college-row-icon-btn college-row-icon-btn--delete"
                                                data-tooltip="Delete"
                                                aria-label="Delete"
                                                data-testid={`delete-${college.college.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveCollege(college.college.id);
                                                }}
                                            >
                                                <svg className="college-row-icon college-row-icon--trash" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M4 7h16" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" />
                                                    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" />
                                                    <path d="M6.5 7l.8 12a2 2 0 0 0 2 1.8h5.4a2 2 0 0 0 2-1.8l.8-12" stroke="#6b7280" strokeWidth="1.8" strokeLinejoin="round" />
                                                    <path d="M10 11v6M14 11v6" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" />
                                                </svg>
                                            </button>
                                            )}
                                        </div>
                                    </div>
                                    {activeCollegeInsight?.collegeId === college.college.id && (
                                        <div className="college-row-insight">
                                            {loadingInsightCollegeId === college.college.id ? (
                                                <div className="college-row-insight-empty">Loading...</div>
                                            ) : activeCollegeInsight.type === "deadlines" ? (
                                                <div>
                                                    <div className="college-row-insight-header">
                                                        <h4>Application deadlines</h4>
                                                        <button
                                                            type="button"
                                                            className="college-row-insight-close"
                                                            onClick={() => setActiveCollegeInsight(null)}
                                                        >
                                                            Close
                                                        </button>
                                                    </div>
                                                    <p className="college-aid-disclaimer">
                                                        Application deadlines are published by each college (and sometimes differ by program and applicant type).
                                                        Polaris links you to official sources so you can confirm dates directly.
                                                    </p>
                                                    <ul className="college-link-list">
                                                        {collegeAdmissionsUrl(college.college.id) && (
                                                            <li>
                                                                <a href={collegeAdmissionsUrl(college.college.id)!} target="_blank" rel="noopener noreferrer">
                                                                    Official college website (start at Admissions)
                                                                </a>
                                                            </li>
                                                        )}
                                                        <li>
                                                            <a href="https://www.commonapp.org/explore" target="_blank" rel="noopener noreferrer">
                                                                Common App college search (deadlines vary by school)
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a href="https://bigfuture.collegeboard.org/plan-for-college/applying-to-college" target="_blank" rel="noopener noreferrer">
                                                                College Board applying-to-college guidance (includes deadline planning resources)
                                                            </a>
                                                        </li>
                                                    </ul>
                                                    {!collegeAdmissionsUrl(college.college.id) && (
                                                        <div className="college-row-insight-empty">
                                                            We don't have this college's official website URL on file yet, so start with Common App / College Board links above.
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="college-row-insight-header">
                                                        <h4>Scholarships</h4>
                                                        <button
                                                            type="button"
                                                            className="college-row-insight-close"
                                                            onClick={() => setActiveCollegeInsight(null)}
                                                        >
                                                            Close
                                                        </button>
                                                    </div>
                                                    <p className="college-aid-disclaimer">
                                                        Named institutional scholarships are published by each college. Polaris can't reliably infer them from national aggregates,
                                                        so this section focuses on official places to find and apply for aid and scholarships.
                                                    </p>
                                                    <ul className="college-link-list">
                                                        {collegeAdmissionsUrl(college.college.id) && (
                                                            <li>
                                                                <a href={collegeAdmissionsUrl(college.college.id)!} target="_blank" rel="noopener noreferrer">
                                                                    Official college website (Financial Aid / Scholarships)
                                                                </a>
                                                            </li>
                                                        )}
                                                        <li>
                                                            <a href="https://studentaid.gov/apply-for-aid/fafsa" target="_blank" rel="noopener noreferrer">
                                                                FAFSA (federal student aid application)
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a href="https://studentaid.gov/understand-aid/types/scholarships" target="_blank" rel="noopener noreferrer">
                                                                Federal Student Aid: Types of scholarships
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a href="https://bigfuture.collegeboard.org/scholarship-search" target="_blank" rel="noopener noreferrer">
                                                                College Board scholarship search
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </React.Fragment>
                                )}
                                </SortableCollegeRow>
                                );
                            })}
                            </div>
                            </SortableContext>
                            </DndContext>

                            {unstarredColleges.length > COLLEGES_PER_PAGE && (
                                <div className="pagination college-list-pagination" data-testid="college-list-pagination">
                                    <div className="pagination-info">
                                        Showing <strong>{collegePageStart}-{collegePageEnd}</strong> of{" "}
                                        <strong>{unstarredColleges.length}</strong> other colleges
                                    </div>
                                    <div className="pagination-controls">
                                        <button
                                            type="button"
                                            className="page-btn"
                                            onClick={() => setCurrentCollegePage((prev) => Math.max(prev - 1, 1))}
                                            disabled={currentCollegePage <= 1}
                                            aria-label="Previous page"
                                        >
                                            Previous
                                        </button>
                                        {Array.from({ length: totalCollegePages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                type="button"
                                                className={`page-number ${page === currentCollegePage ? "page-number-active" : ""}`}
                                                onClick={() => setCurrentCollegePage(page)}
                                                aria-label={`Page ${page}`}
                                                aria-current={page === currentCollegePage ? "page" : undefined}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            className="page-btn"
                                            onClick={() => setCurrentCollegePage((prev) => Math.min(prev + 1, totalCollegePages))}
                                            disabled={currentCollegePage >= totalCollegePages}
                                            aria-label="Next page"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* Featured Resources Section - hidden when the school disables college counseling resources */}
                {collegeResourcesEnabled && (
                    <section className="featured-resources-section" data-testid="college-planning-resources">
                        <h2>Resources</h2>
                        <FeaturedContentGrid limit={6} />
                    </section>
                )}

                {/* Add College Modal */}
                <AddCollegeModal
                    isOpen={isAddCollegeModalOpen}
                    onClose={handleCloseModal}
                    onAddCollege={handleAddCollegeToList}
                    athleteUsername={athleteUsername}
                />

                {/* Video Lightbox for course videos */}
                <VideoLightbox
                    open={!!selectedVideo}
                    resource={selectedVideo}
                    onClose={() => setSelectedVideo(null)}
                    onVideoEnd={handleVideoEnd}
                    analytics={selectedVideo ? courseVideoAnalytics(COLLEGE_PLANNING_TAB, selectedVideo.id) : null}
                />

                {/* Course Tips Modal - for course launch tips */}
                <CourseTipsModal
                    isOpen={isCourseTipsModalOpen}
                    onClose={() => setIsCourseTipsModalOpen(false)}
                    onLaunchCourse={handleLaunchCourseAfterTips}
                    courseTitle={pendingCourse?.title}
                    modalHeading={pendingCourse?.launchModalHeading}
                    modalContent={pendingCourse?.launchModalContent}
                    modalButtonLabel={pendingCourse?.launchModalButtonLabel}
                />

                {/* Course Completion Modal - shown when video ends */}
                <CourseCompletionModal
                    isOpen={isCompletionModalOpen}
                    onClose={() => setIsCompletionModalOpen(false)}
                    modalHeading={pendingCourse?.courseCompletionModalHeading}
                    modalImageUrl={pendingCourse?.courseCompletionModalImageUrl}
                    modalContent={pendingCourse?.courseCompletionModalContent}
                    modalBadgeUrl={pendingCourse?.courseCompletionModalBadgeUrl}
                    modalDownloadUrl={pendingCourse?.courseCompletionModalDownloadUrl}
                    modalCloseLabel={pendingCourse?.courseCompletionModalCloseLabel}
                    modalLinkText={pendingCourse?.courseCompletionModalLinkText}
                    modalIconUrl={pendingCourse?.courseCompletionModalIconUrl}
                />

                {/* Expert Modal - shown when Learn More is clicked */}
                <ExpertModal
                    isOpen={isExpertModalOpen}
                    onClose={() => setIsExpertModalOpen(false)}
                    expert={expert}
                />

                {/* College Notes Panel */}
                <CollegeNotesPanel
                    isOpen={notesPanel.isOpen}
                    onClose={handleCloseNotes}
                    collegeName={notesPanel.collegeName}
                    collegeId={notesPanel.collegeId}
                    athleteId={athleteNumericId}
                    notes={notesPanel.collegeId !== null ? (collegeNotes[notesPanel.collegeId] || []) : []}
                    onAddNote={handleAddNote}
                    onUpdateNote={handleUpdateNote}
                    onDeleteNote={handleDeleteNote}
                    onNotesLoaded={handleNotesLoaded}
                    readOnly={guardianReadOnly}
                />

                <CollegeContactsPanel
                    isOpen={contactsPanel.isOpen}
                    onClose={handleCloseContacts}
                    collegeName={contactsPanel.collegeName}
                    collegeId={contactsPanel.collegeId}
                    athleteId={athleteNumericId}
                    contacts={contactsPanel.collegeId !== null ? (collegeContacts[contactsPanel.collegeId] || []) : []}
                    onContactsLoaded={handleContactsLoaded}
                    onDeleteContact={handleDeleteContact}
                    readOnly={guardianReadOnly}
                />
            </div>
        </div>
    );
}
